import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Timeline } from "@mui/icons-material";
import { AgGridTable } from "../../Component/AgGridTable";
import StickyFooterButtons from "../../Component/StickyFooterButtons";
import {
  GetInvoiceDetailsByID,
  UpdateInvoice,
  deleteInvoice,
} from "../../services/InvoiceServices";
import {
  invoiceDetails,
  GetAllInvoices,
  PdfDetails,
  InvoiceResponse,
} from "../../Interface/Invoice";
import { ColDef } from "ag-grid-community";
import { useNavigate, useLocation } from "react-router-dom";
import InvoiceSummaryCard from "../../Component/Summarycard";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import logo from "../../Assets/Images/cvt logo.png";
import { CellInput } from "jspdf-autotable";

interface ViewInvoiceTableProps {
  drawerOpen: boolean;
}

const ViewInvoice: React.FC<ViewInvoiceTableProps> = ({ drawerOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedRowData: GetAllInvoices = location.state?.selectedRowData;
  const [editTableData, setEditTableData] = useState<invoiceDetails[]>([]);
  const [pdfData, setPdfData] = useState<PdfDetails | null>(null);
  const [pdfDetails, setPdfDetails] = useState<PdfDetails | null>(null);
  const [invoiceMonth, setInvoiceMonth] = useState<string | null>(null);
  const [invoiceYear, setInvoiceYear] = useState<string | null>(null);

  const [isGridEditable, setIsGridEditable] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const gridApiRef = useRef<any>(null);
  const numberToWords = require("number-to-words");
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const handleGenerateClick = () => {
    setOpenDownloadDialog(true);
  };
  const confirmDownload = () => {
    setOpenDownloadDialog(false);
    handleDownloadPDF();
  };

  const loadEmployeeDetails = async (id: number) => {
    try {
      const response = await GetInvoiceDetailsByID(id);
      const details = response.data?.invoiceDetails ?? [];
      const formatted = details.map((emp: any) => ({
        timesheetId: emp.timesheetId,
        employeeId: emp.employeeId,
        firstName: emp.firstName,
        lastName: emp.lastName,
        bookedHours: emp.bookedHours,
        rate: emp.rate,
        invoiceAmount: emp.invoiceAmount,
      }));
      setEditTableData(formatted);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
    }
  };

  const loadPdfDetails = async (id: number) => {
    try {
      const response = await GetInvoiceDetailsByID(id);
      const pdfDetails = response?.data?.pdfDetails;

      if (pdfDetails) {
        setPdfData(pdfDetails);
        console.log("Loaded PDF Details:", pdfDetails);
      } else {
        console.warn("No PDF details found.");
      }
    } catch (error) {
      console.error("Error loading PDF details:", error);
    }
  };

  useEffect(() => {
    const fetchInvoiceMonthYear = async () => {
      try {
        if (selectedRowData?.invoiceId) {
          const response: InvoiceResponse = await GetInvoiceDetailsByID(
            selectedRowData.invoiceId
          );

          if (response?.data) {
            setInvoiceMonth(response.data.month);
            setInvoiceYear(response.data.year);
            console.log(
              "Month:",
              response.data.month,
              "Year:",
              response.data.year
            );
          }
        }
      } catch (error) {
        console.error("Error fetching invoice month/year:", error);
      }
    };

    fetchInvoiceMonthYear();
  }, [selectedRowData?.invoiceId]);

  useEffect(() => {
    if (selectedRowData?.invoiceId) {
      loadEmployeeDetails(selectedRowData.invoiceId);
      loadPdfDetails(selectedRowData.invoiceId);
    }
  }, [selectedRowData]);

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      const response = await GetInvoiceDetailsByID(selectedRowData.invoiceId);
      if (response?.data?.pdfDetails) {
        setPdfDetails(response.data.pdfDetails);
      }
    };
    fetchInvoiceDetails();
  }, [selectedRowData.invoiceId]);

  const handleUpdate = () => {
    if (!isGridEditable) {
      setIsGridEditable(true);
      setTimeout(() => {
        gridApiRef.current?.startEditingCell({
          rowIndex: 0,
          colKey: "bookedHours",
        });
      }, 100);
    } else {
      setOpenConfirmDialog(true);
    }
  };

  const confirmUpdate = async () => {
    try {
      const payload = {
        invoiceId: selectedRowData.invoiceId,
        timesheetId: selectedRowData.timesheetId,
        monthId: selectedRowData.monthId,
        yearId: selectedRowData.yearId,
        projectId: selectedRowData.projectId,
        customerId: selectedRowData.customerId,
        invoiceDetailRequestModel: editTableData.map((row) => ({
          invoiceId: selectedRowData.invoiceId,
          employeeId: row.employeeId,
          bookedHours: row.bookedHours,
          rate: row.rate,
          invoiceAmount: row.invoiceAmount,
        })),
      };

      await UpdateInvoice(selectedRowData.invoiceId, payload);
      setIsGridEditable(false);
      setSnackbarMessage("Invoice updated successfully!");
      setOpenSnackbar(true);
      setOpenConfirmDialog(false);
      setTimeout(() => {
        navigate("/invoicetable");
      }, 1500);
    } catch (error) {
      console.error("Error updating invoice details:", error);
      setSnackbarMessage("Failed to update invoice.");
      setOpenSnackbar(true);
      setOpenConfirmDialog(false);
    }
  };

  const handleDelete = () => setOpenDeleteDialog(true);

  const confirmDelete = async () => {
    try {
      await deleteInvoice(selectedRowData.invoiceId);
      setSnackbarMessage("Invoice deleted successfully!");
      setOpenSnackbar(true);
      setOpenDeleteDialog(false);

      setTimeout(() => navigate("/invoicetable"), 1500);
    } catch (error) {
      console.error("Error deleting invoice:", error);
      setSnackbarMessage("Failed to delete invoice.");
      setOpenSnackbar(true);
      setOpenDeleteDialog(false);
    }
  };

  const getBase64ImageFromLocalImport = (imgPath: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = imgPath;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("No canvas context");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };

      img.onerror = (err) => reject("Image load error: " + err);
    });
  };

  const addFooter = (doc: jsPDF) => {
    const baseY = 275;
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;

    // Draw full-width separator line
    doc.setDrawColor(180);
    doc.line(0, baseY, pageWidth, baseY);

    // Title
    doc.setFont("Aptos", "bold");
    doc.setFontSize(12);
    doc.setTextColor(60);
    doc.text("CognitiveValueTech Private Limited", centerX, baseY + 6, {
      align: "center",
    });

    // Address
    doc.setFont("Aptos", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.text(
      "7th Floor, A1 Gateway Office Parks IT/ITES SEZ, No.16 GST Road, Perungalathur, Chennai, Tamilnadu - 600063, India.",
      centerX,
      baseY + 12,
      { align: "center" }
    );

    // Contact Line Parts
    const text1 = "Ph. No: +91 9884817258 | ";
    const email = "contactus@cognitivevaluetech.com";
    const separator = " | ";
    const website = "www.cognitivevaluetech.com";

    // Measure total width
    doc.setFontSize(10);
    doc.setFont("Aptos", "normal");

    const width1 = doc.getTextWidth(text1);
    const width2 = doc.getTextWidth(email);
    const widthSep = doc.getTextWidth(separator);
    const width3 = doc.getTextWidth(website);

    const totalWidth = width1 + width2 + widthSep + width3;
    let x = (pageWidth - totalWidth) / 2;
    const y = baseY + 18;

    // Draw each segment
    doc.setTextColor(60);
    doc.text(text1, x, y);
    x += width1;

    doc.setTextColor(0, 102, 204);
    doc.textWithLink(email, x, y, {
      url: "mailto:contactus@cognitivevaluetech.com",
    });
    x += width2;

    doc.setTextColor(60);
    doc.text(separator, x, y);
    x += widthSep;

    doc.setTextColor(0, 102, 204);
    doc.textWithLink(website, x, y, {
      url: "https://www.cognitivevaluetech.com",
    });
  };

  const handleDownloadPDF = async () => {
    const doc = new jsPDF();
    const amount = selectedRowData.totalInvoiceAmount || 0;
    const amountInWords =
      numberToWords
        .toWords(amount)
        .replace(/^\w/, (c: string) => c.toUpperCase()) +
      " Dollars and No Cents";

    // Calculate previous month's start and end date
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1); // first day of previous month
    const endOfPrevMonth = new Date(
      prevMonth.getFullYear(),
      prevMonth.getMonth() + 1,
      0
    ); // last day
    const formatDate = (date: Date) => {
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const yyyy = date.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    };

    const startDate = formatDate(prevMonth);
    const endDate = formatDate(endOfPrevMonth);

    const logoBase64 = await getBase64ImageFromLocalImport(logo);
    doc.addImage(logoBase64, "PNG", 20, 7, 50, 13);
    doc.setFontSize(10);
    // Title
    doc.setFont("Aptos", "bold");
    doc.setFontSize(16);
    doc.text("TAX INVOICE", 100, 30, { align: "center" });

    doc.setFontSize(10);
    const leftX = 20;
    const rightX = 130;
    const labelGapLeft = 20;
    const labelGapRight = 25;
    const valueXLeft = leftX + labelGapLeft;
    const valueXRight = rightX + labelGapRight;
    let baseY = 50;
    const lineHeight = 6;

    doc.setFont("Aptos", "normal");

    // LEFT COLUMN LABELS
    doc.text("Name        :", leftX, baseY);
    doc.text("Firm         :", leftX, baseY + lineHeight);
    doc.text("Address    :", leftX, baseY + lineHeight * 2);

    // LEFT COLUMN VALUES
    doc.setFont("Aptos", "bold");
    doc.text(pdfDetails?.customerName || "", valueXLeft, baseY);
    doc.text(`${pdfDetails?.firmName || ""}`, valueXLeft, baseY + lineHeight);

    // Multi-line Address
    const address = pdfDetails?.invoiceAddress;
    if (address) {
      const line1 = [address.street1, address.street2]
        .filter(Boolean)
        .join(", ");
      const line2 = [address.city, address.state].filter(Boolean).join(", ");
      const line3 = [address.postalCode, address.country]
        .filter(Boolean)
        .join(", ");

      const fullAddress = [line1, line2, line3].filter(Boolean); // Exclude empty lines

      fullAddress.forEach((line, index) => {
        doc.text(line, valueXLeft, baseY + lineHeight * (2 + index));
      });
    }

    // RIGHT COLUMN LABELS
    doc.setFont("Aptos", "normal");
    doc.text("Inv. No           :", rightX, baseY);
    doc.text("Inv. Date        :", rightX, baseY + lineHeight);
    doc.text("PAN NO         :", rightX, baseY + lineHeight * 2);
    doc.text("GST NO         :", rightX, baseY + lineHeight * 3);
    doc.text("LUT No          :", rightX, baseY + lineHeight * 4);
    doc.text("Date                :", rightX, baseY + lineHeight * 5);

    // Format today's date
    const today = new Date();
    const formattedDate = `${today.getDate().toString().padStart(2, "0")}/${(
      today.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${today.getFullYear()}`;

    // RIGHT COLUMN VALUES
    doc.setFont("Aptos", "bold");
    doc.text(`${pdfDetails?.invoiceNumber || ""}`, valueXRight, baseY);
    doc.text(
      `${pdfDetails?.invoiceDate || ""}`,
      valueXRight,
      baseY + lineHeight
    );
    doc.text("AAJCC1031F", valueXRight, baseY + lineHeight * 2);
    doc.text("33AACJC1031F2ZA", valueXRight, baseY + lineHeight * 3);
    doc.text("AD3033251539318", valueXRight, baseY + lineHeight * 4);
    doc.text("26th Mar", valueXRight, baseY + lineHeight * 5);

    autoTable(doc, {
      startY: 93,
      head: [["S. No", "Description of Services", "Service code", "Amount"]],
      body: [
        [
          "1",
          `Software Consulting Services for the billing period of\n${startDate} – ${endDate}`,
          "998313",
          `$${amount.toLocaleString("en-IN")}`,
        ],
        ["", "", "Total", `$${amount.toLocaleString("en-IN")}`],
        ["", "", "IGST @ 0%", "$0"],
        [
          {
            content: `USD: ${amountInWords}`,
            colSpan: 2,
            styles: { fontStyle: "bold", halign: "left" },
          },
          { content: "Grand Total", styles: { halign: "center" } },
          {
            content: `$${amount.toLocaleString("en-IN")}`,
            styles: { fontStyle: "bold", halign: "right" },
          },
        ],
      ],
      styles: {
        fontSize: 10,
        textColor: [0, 0, 0],
        cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      headStyles: {
        halign: "center",
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 20 },
        1: { halign: "center", cellWidth: 90 },
        2: { halign: "center", cellWidth: 30 },
        3: { halign: "right", cellWidth: 30 },
      },
      margin: { top: 10, bottom: 10, left: 20, right: 20 },
      theme: "grid",

      didParseCell: function (data) {
        const rowIndex = data.row.index;
        const colIndex = data.column.index;

        // Common body row styles
        if (rowIndex >= 1) {
          data.cell.styles.fontSize = 9;
          data.cell.styles.fontStyle = "bold";
        }

        // USD row formatting
        if (rowIndex === 3) {
          if (colIndex === 0) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.halign = "center";
          }
          if (colIndex === 1) {
            data.cell.styles.fontStyle = "normal";
          }
          if (colIndex === 2) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.halign = "center";
          }
          if (colIndex === 3) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.halign = "right";
          }
        }

        // Remove line between Total and IGST @ 0%
        if (rowIndex === 1) {
          data.cell.styles.lineWidth = {
            top: 0.1,
            right: 0.1,
            bottom: 0,
            left: 0.1,
          };
        }
        if (rowIndex === 2) {
          data.cell.styles.lineWidth = {
            top: 0,
            right: 0.1,
            bottom: 0.1,
            left: 0.1,
          };
        }
        if ((rowIndex === 1 || rowIndex === 2) && data.column.index !== 3) {
          const currentWidth =
            typeof data.cell.styles.lineWidth === "number"
              ? {
                  top: data.cell.styles.lineWidth,
                  right: data.cell.styles.lineWidth,
                  bottom: data.cell.styles.lineWidth,
                  left: data.cell.styles.lineWidth,
                }
              : data.cell.styles.lineWidth || {
                  top: 0.1,
                  right: 0.1,
                  bottom: 0.1,
                  left: 0.1,
                };

          // Keep top and bottom as they are, remove vertical lines inside
          data.cell.styles.lineWidth = {
            top: currentWidth.top,
            right: 0,
            bottom: currentWidth.bottom,
            left: data.column.index === 0 ? currentWidth.left : 0,
          };
        }
      },
    });

    let y = (doc as any).lastAutoTable.finalY + 15;

    doc.setFontSize(10);
    doc.setFont("Aptos", "normal");
    doc.text(
      "We trust that you will find our invoice satisfactory and look forward to working with you.",
      20,
      y
    );

    const labelX = 20;
    const valueX = 60;
    let lineY = y + 10;

    doc.setFont("Aptos", "normal");
    doc.text("Payment Terms:", labelX, lineY);
    doc.setFont("Aptos", "bold");
    doc.text(pdfDetails?.payTermsCode || "", valueX, lineY);

    lineY += 7;
    doc.setFont("Aptos", "normal");
    doc.text("Invoice Due Date:", labelX, lineY);
    doc.setFont("Aptos", "bold");
    doc.text(pdfDetails?.invoiceDueDate || "", valueX, lineY);

    lineY += 7;
    doc.setFont("Aptos", "normal");
    doc.text("Purpose of remittance:", labelX, lineY);
    doc.setFont("Aptos", "bold");
    doc.text("Professional Software services", valueX, lineY);

    //  Bank Particulars

    let tableY = y + 30;
    autoTable(doc, {
      startY: tableY + 5,
      head: [
        [
          {
            content: "Bank Particulars",
            styles: {
              font: "aptos",
              halign: "center",
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              fontStyle: "bold",
              fontSize: 10,
              lineWidth: 0.1,
              lineColor: [0, 0, 0],
            },
          },
          {
            content: "Beneficiary",
            styles: {
              font: "aptos",
              halign: "center",
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              fontStyle: "bold",
              fontSize: 10,
              lineWidth: 0.1,
              lineColor: [0, 0, 0],
            },
          },
        ],
      ],
      body: [
        [
          {
            content:
              "Bank                :   ICICI Bank\n" +
              "Branch             :   Chrompet, Chennai - 600044\n" +
              "Account No.    :   318505000799\n" +
              "IFSC Code      :   ICIC0003185\n" +
              "SWIFT Code   :   ICICINBBCTS\n" +
              "AD Code         :   6344041-00J00A7",
            styles: {
              font: "aptos",
              fontSize: 10,
              halign: "left",
              cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
            },
          },
          {
            content:
              "Cognitive Value Technologies Pvt Ltd\n" +
              "7th Floor, A1 Gateway Office Parks IT/ITES SEZ\n" +
              "No.16 GST Road, Perungalathur\n" +
              "Chennai, Tamilnadu - 600063, India",
            styles: {
              font: "aptos",
              fontSize: 10,
              halign: "left",
              cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
            },
          },

          {
            content:
              "CognitiveValueTech Private Limited\n" +
              "7 th Floor, A1 Gateway Office Parks IT/ITES SEZ,\n" +
              "No.16 GST Road, Perungalathur,\n" +
              "Chennai, Tamil Nadu - 600 063, India",
            styles: {
              font: "Aptos",
              fontSize: 10,
              halign: "left",
              cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
            },
          },
        ],
      ],
      theme: "grid",
      styles: {
        font: "Aptos",
        textColor: [0, 0, 0],
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      margin: { left: 20, right: 20 },
    });

    const footerY = (doc as any).lastAutoTable.finalY + 8;
    const pageWidth = doc.internal.pageSize.getWidth();
    const leftMargin = 20;
    const rightMargin = 20;
    const usableWidth = pageWidth - leftMargin - rightMargin;

    doc.setFontSize(10);
    doc.setFont("Aptos", "normal");
    doc.setTextColor(30);

    // First Line
    const text1 =
      "Supply Meant for Export Under Bond or Letter of Undertaking for Authorised operations without Payment of Integrated";
    const wrappedText1 = doc.splitTextToSize(text1, usableWidth);
    doc.text(wrappedText1, leftMargin, footerY);

    // Second Line
    const text2 =
      'Tax (IGST) (IN ACCORDANCE WITH PROVISIONS OF SEC.16(1)(a) of IGST ACT, EXPORT SERVICES ARE COVERED UNDER "ZERO RATED SUPPLY")';
    const wrappedText2 = doc.splitTextToSize(text2, usableWidth);
    doc.text(wrappedText2, leftMargin, footerY + 5 * wrappedText1.length);

    // Footer Page 1
    addFooter(doc);

    //  PAGE 2
    doc.addPage();

    // Add logo on top left
    doc.addImage(logoBase64, "PNG", 20, 7, 50, 13);
    // table body
    const tableBody: CellInput[][] = editTableData.map((row, index) => [
      { content: index + 1, styles: { halign: "center" } },
      {
        content: `${row.firstName} ${row.lastName}`,
        styles: { halign: "center" },
      },
      { content: row.role || "Developer", styles: { halign: "center" } },
      { content: row.bookedHours.toString(), styles: { halign: "center" } },
      { content: `$${row.rate}`, styles: { halign: "center" } },
      {
        content: `$${row.invoiceAmount.toLocaleString("en-IN")}`,
        styles: { halign: "right" },
      },
    ]);

    const totalAmount = editTableData.reduce(
      (sum, row) => sum + row.invoiceAmount,
      0
    );

    // Add total row
    tableBody.push([
      "",
      "",
      "",
      "",
      { content: "Total", styles: { halign: "center", fontStyle: "bold" } },
      {
        content: `$${totalAmount.toLocaleString("en-IN")}`,
        styles: { halign: "right", fontStyle: "bold" },
      },
    ]);

    // Add USD row
    tableBody.push([
      {
        content: `USD: ${amountInWords}`,
        colSpan: 6,
        styles: { fontStyle: "bold", halign: "left" },
      },
    ]);
    autoTable(doc, {
      startY: 50,
      head: [["S. No", "Resources", "Role", "Hours", "Hourly", "Total"]],
      body: tableBody,
      theme: "grid",
      styles: {
        fontSize: 10,
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        font: "aptos",
        halign: "center",
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 10,
        cellPadding: 4,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      margin: { left: 20, right: 20, bottom: 30, top: 50 },

      didParseCell: function (data) {
        const totalRowIndex = tableBody.length - 2; // last-before row index (Total row)

        if (data.row.index === totalRowIndex) {
          // First 4 columns: remove vertical lines but keep outer left border
          if (data.column.index === 0) {
            data.cell.styles.lineWidth = {
              top: 0.1,
              right: 0,
              bottom: 0.1,
              left: 0.1,
            };
          }
          if (data.column.index > 0 && data.column.index < 4) {
            data.cell.styles.lineWidth = {
              top: 0.1,
              right: 0,
              bottom: 0.1,
              left: 0,
            };
          }

          // Column 4 ("Total") → vertical line on right
          if (data.column.index === 4) {
            data.cell.styles.lineWidth = {
              top: 0.1,
              right: 0.1,
              bottom: 0.1,
              left: 0,
            };
          }

          // Column 5 (amount) → keep both left and right borders
          if (data.column.index === 5) {
            data.cell.styles.lineWidth = {
              top: 0.1,
              right: 0.1,
              bottom: 0.1,
              left: 0.1,
            };
          }
        }
      },

      didDrawPage: function (data) {
        doc.addImage(logoBase64, "PNG", 20, 7, 50, 13);
        addFooter(doc);
      },
    });

    const invoiceNumber = pdfDetails?.invoiceNumber;

    const fileName = `CVT_Invoice_${invoiceMonth}-${invoiceYear}_${invoiceNumber}`;

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(pdfUrl);
  };

  const editColumns: ColDef[] = [
    { field: "firstName", headerName: "First Name", editable: false },
    { field: "lastName", headerName: "Last Name", editable: false },
    {
      field: "bookedHours",
      headerName: "Hours",
      editable: () => isGridEditable,
      cellClass: "right-align",
      cellEditor: "agNumberCellEditor",
    },
    {
      field: "rate",
      headerName: "Rate",
      editable: false,
      cellClass: "right-align",
      valueFormatter: (params) =>
        `$${(params.value || 0).toLocaleString("en-IN")}`,
    },
    {
      field: "invoiceAmount",
      headerName: "Amount",
      editable: false,
      cellClass: "right-align",
      valueFormatter: (params) =>
        `$${(params.value || 0).toLocaleString("en-IN")}`,
    },
  ];

  return (
    <Box sx={{ padding: 3, backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Timeline sx={{ color: "#1976d2" }} />
        <Typography variant="h5" fontWeight="bold">
          Invoice Details
        </Typography>
      </Box>

      <InvoiceSummaryCard
        invoiceAmount={selectedRowData.totalInvoiceAmount?.toLocaleString(
          "en-IN"
        )}
        totalHours={selectedRowData.totalHours}
        month={selectedRowData.month}
        year={selectedRowData.year}
        customerName={selectedRowData.customerName}
        projectName={selectedRowData.projectName}
      />

      <Box display="flex" gap={2} mt={3} mb={5}>
        <Box flex={2.2}>
          <AgGridTable
            domLayout="autoHeight"
            customStyles="legacy"
            columns={editColumns}
            data={editTableData}
            pagination={false}
            rowSelection="single"
            paginationPerPage={10}
            noDataComponentText="Loading Data"
            gridOptions={{
              onGridReady: (params) => {
                gridApiRef.current = params.api;
              },
              suppressHorizontalScroll: false,
              onCellValueChanged: (params: any) => {
                const rowIndex = params?.node?.rowIndex;
                const updatedData = [...editTableData];
                const row = updatedData[rowIndex];

                const nextRowIndex = rowIndex + 1;
                if (nextRowIndex < updatedData.length) {
                  setTimeout(() => {
                    gridApiRef.current.startEditingCell({
                      rowIndex: nextRowIndex,
                      colKey: "bookedHours",
                    });
                  }, 100);
                }
              },
            }}
            handleSelectedRows={() => {}}
          />
        </Box>
      </Box>

      <StickyFooterButtons
        isEditMode={isGridEditable}
        onEditClick={handleUpdate}
        onDeleteClick={handleDelete}
        onBackClick={() => navigate("/invoicetable")}
        onGenerateClick={handleGenerateClick}
      />

      {/* Confirm Download Dialog */}
      <Dialog
        open={openDownloadDialog}
        onClose={() => setOpenDownloadDialog(false)}
      >
        <DialogTitle>Confirm Download</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to download this invoice?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDownloadDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmDownload} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to update this invoice?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmUpdate} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this invoice? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewInvoice;
function addFooter(doc: jsPDF) {
  throw new Error("Function not implemented.");
}
